// WHAT: Component displaying key roadmap metrics (total, completion rate, priority, impact)
// WHY:  Provides primary metrics overview for roadmap analytics dashboard
// DOMAIN: Admin → Roadmap → Analytics
// DEPENDENCIES: Card, Badge components, Lucide icons
// RISKS:
//   - NOTE: Dynamic Tailwind classes may not work with JIT compiler
//   - NOTE: completionRate is formatted to 1 decimal - may lose precision
//   - NOTE: Uses index as key in map - consider using unique identifier

import { LucideActivity, LucideTrendingUp, LucideTarget, LucideZap } from "lucide-react";
import React from "react";

import { Card, Badge } from "@/components/ui";

/**
 * Props for KeyMetrics component
 */
interface KeyMetricsProperties {
  /** Completion rate as percentage (0-100) */
  completionRate: number;
  /** Number of high impact items */
  highImpact: number;
  /** Number of high priority items */
  highPriority: number;
  /** Total number of roadmap items */
  total: number;
}

const getMetricVariantColor = (variant: "accent" | "primary" | "success" | "warning"): string => {
  if (variant === "primary") {
    return "tracq-bright-blue";
  }
  if (variant === "success") {
    return "green-500";
  }
  if (variant === "accent") {
    return "tracq-deep-orange";
  }
  return "orange-500";
};

/**
 * WHAT:
 *   Component that displays key roadmap metrics in a grid of cards showing
 *   total items, completion rate, high priority, and high impact counts.
 *
 * WHY:
 *   Provides primary metrics overview for quick assessment of roadmap status
 *   and progress.
 *
 * PROPS:
 *   - total: number — Total number of roadmap items
 *   - completionRate: number — Completion percentage (0-100)
 *   - highPriority: number — Count of high priority items
 *   - highImpact: number — Count of high impact items
 *
 * BEHAVIOR:
 *   - Renders four metric cards in responsive grid (1/2/4 columns)
 *   - Formats completion rate to 1 decimal place with % symbol
 *   - Uses variant colors for visual distinction
 *
 * RISKS:
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly - use static classes
 *   - NOTE: completionRate formatting may lose precision for very small values
 *   - NOTE: Index used as key - should use unique identifier if metrics array changes
 *
 * @param root0
 *
 * @param root0.total
 *
 * @param root0.completionRate
 *
 * @param root0.highPriority
 *
 * @param root0.highImpact
 *
 * @returns {JSX.Element} Die gerenderte KeyMetrics-Komponente
 *
 */
interface MetricItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  variant: "accent" | "primary" | "success" | "warning";
}

export const KeyMetrics: React.FC<KeyMetricsProperties> = ({
  completionRate,
  highImpact,
  highPriority,
  total,
}) => {
  const metrics: MetricItem[] = [
    {
      icon: LucideActivity,
      label: "Total Items",
      value: total,
      variant: "primary" as const,
    },
    {
      icon: LucideTrendingUp,
      label: "Completion Rate",
      value: `${completionRate.toFixed(1)}%`,
      variant: "success" as const,
    },
    {
      icon: LucideTarget,
      label: "High Priority",
      value: highPriority,
      variant: "accent" as const,
    },
    {
      icon: LucideZap,
      label: "High Impact",
      value: highImpact,
      variant: "warning" as const,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric: MetricItem) => {
        const IconComponent = metric.icon;
        const variantColor = getMetricVariantColor(metric.variant);
        return (
          <Card className="text-center" key={metric.label} padding="md" variant="default">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-${variantColor}/10`}>
                <IconComponent className={`w-5 h-5 text-${variantColor}`} />
              </div>
              <div className="text-left">
                <Badge className="mb-1" size="sm" style="subtle" variant={metric.variant}>
                  {metric.label}
                </Badge>
                <div className="text-xl font-bold text-white">{metric.value}</div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
